/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { IModuleProps, Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import {
    ICheckoutDeliveryOption,
    ICheckoutDeliveryOptionEdit,
    ICheckoutDeliveryOptionsError,
    ICheckoutDeliveryOptionsList,
    ICheckoutDeliveryOptionsViewState
} from '@msdyn365-commerce-modules/checkout/src/modules/checkout-delivery-options/./checkout-delivery-options';
import { ICheckoutDeliveryOptionsProps } from '@msdyn365-commerce-modules/checkout/src/modules/checkout-delivery-options/checkout-delivery-options.props.autogenerated';
import { ICheckoutDeliveryOptionsData } from '@msdyn365-commerce-modules/checkout/src/modules/checkout-delivery-options/checkout-delivery-options.data';
import { DeliveryOption } from '@msdyn365-commerce/retail-proxy';
import { getGdnAddChargeLineAsync } from '../../../actions/DataActionExtension.g';
import { IChargeLinesBreakdownData } from '../../../utils/chargeLinesHelper';

export interface ICustomCheckoutDeliveryOptionsViewProps extends ICheckoutDeliveryOptionsProps<ICheckoutDeliveryOptionsData> {
    CheckoutDeliveryOptions: IModuleProps;
    selectedItemId: string;
    deliveryOptionsData: DeliveryOption[];
    isLoading: boolean;
    errorMessageData: string;
    viewState: ICheckoutDeliveryOptionsViewState;
    deliveryOptions?: ICheckoutDeliveryOptionsList;
    deliveryOptionSelected?: ICheckoutDeliveryOption;
    errorMessage?: ICheckoutDeliveryOptionsError;
    waiting?: React.ReactNode;
    saveButton?: React.ReactNode;
    editButton?: React.ReactNode;
    cancelButton?: React.ReactNode;
    alert?: React.ReactNode;
    checkoutErrorRef?: React.RefObject<HTMLElement>;
    checkoutCartLineGroupDeliveryOptions?: React.ReactNode;
    enableCheckoutCartLineDeliveryOptions?: boolean;
    cartLineImages?: React.ReactNode;
    onDeliveryOptionChange(event: React.ChangeEvent<HTMLInputElement>): void;
    onSave(): void;
    onEdit(): void;
    onCancel(): void;
}

const DeliveryOptionList: React.FC<ICheckoutDeliveryOptionsList> = ({ DeliveryOptionsList, list }) => {
    if (!list || list.length === 0) {
        return null;
    }

    return (
        <Node {...DeliveryOptionsList}>
            {list.map((deliveryOption: ICheckoutDeliveryOptionEdit) => {
                return (
                    <Node {...deliveryOption.DeliveryOption} key={deliveryOption.code} aria-label='Please select a delivery option.'>
                        {deliveryOption.radioButton}
                        {deliveryOption.description}
                        {/* {deliveryOption.price} */}
                    </Node>
                );
            })}
        </Node>
    );
};

const DeliveryOptionSelected: React.FC<ICheckoutDeliveryOption> = ({ DeliveryOption, description, price }) => {
    return (
        <Node {...DeliveryOption}>
            {description}
            {/* {price} */}
        </Node>
    );
};

const CheckoutDeliveryOptionsView: React.FC<ICustomCheckoutDeliveryOptionsViewProps> = props => {
    const {
        CheckoutDeliveryOptions,
        viewState,
        deliveryOptions,
        deliveryOptionSelected,
        alert,
        waiting,
        saveButton,
        editButton,
        cancelButton,
        data,
        context
    } = props;

    const chargeLineCodeToAdd: string = context.app.config.chargeLineCodeToAdd || '';
    const chargeLineAmountPercentage: number = parseFloat(props.context.app.config.chargeLineAmountPercent || '0');
    const chargeLinesBreakdown: IChargeLinesBreakdownData[] = props.context.app.config.chargeLinesBreakdown || [];
    const taxableChargeCodes = chargeLinesBreakdown
        .filter(breakdown => breakdown.isTaxableLine && breakdown.chargeCode !== chargeLineCodeToAdd)
        .map(b => b.chargeCode);

    React.useEffect(() => {
        if (!data?.checkout.result?.checkoutCart.cart) {
            return;
        }
        if (!chargeLineCodeToAdd || chargeLineAmountPercentage === 0) {
            return;
        }

        const run = async () => {
            const cart = data.checkout.result?.checkoutCart.cart;
            let workingSubtotal = cart?.SubtotalAmount || 0;

            const cartChargeLines = cart?.ChargeLines || [];
            const cartLinesChargeLines = (cart?.CartLines?.map(item => Array.from(item.ChargeLines || [])) || []).flat();
            const allChargeLines = [...cartChargeLines, ...cartLinesChargeLines];
            const taxableChargeLines = allChargeLines.filter(line => taxableChargeCodes.includes(line.ChargeCode || '')) || [];

            if (taxableChargeLines) {
                const taxableTotal = taxableChargeLines.reduce((sum, line) => sum + (line.CalculatedAmount || 0), 0);
                workingSubtotal += taxableTotal;
            }

            const amountToAdd = parseFloat((workingSubtotal * (chargeLineAmountPercentage / 100)).toFixed(2));

            const existing = cart?.ChargeLines?.find(l => l.ChargeCode === chargeLineCodeToAdd);
            const existingAmount = existing?.CalculatedAmount || 0;

            if (Math.abs(existingAmount - amountToAdd) < 0.005) {
                return;
            }
            if (cart?.Id) {
                await getGdnAddChargeLineAsync({ callerContext: context.actionContext }, cart.Id, amountToAdd, chargeLineCodeToAdd);
                await data.checkout.result?.checkoutCart.refreshCart({});
            }
        };

        run();
    }, [data?.checkout.result?.checkoutCart.cart.ChargeLines]);

    return (
        // TODO: All wrapper div should be provided by viewProps. Once SDK provide the sln, update markup.
        <Module {...CheckoutDeliveryOptions}>
            {viewState.isLoading && waiting}
            {viewState.isError && alert}
            {viewState.isShowList && deliveryOptions && <DeliveryOptionList {...deliveryOptions} />}
            {viewState.isShowSelected && deliveryOptionSelected && <DeliveryOptionSelected {...deliveryOptionSelected} />}
            {viewState.isShowSaveButton && saveButton}
            {viewState.isShowEditButton && editButton}
            {viewState.isShowCancelButton && cancelButton}
        </Module>
    );
};

export default CheckoutDeliveryOptionsView;
